// buildCatchAllFacetFields.js

import { resolveEnumOptionsForField } from 'utilities/EnumOptions';

const LENS_OPTION_FIELD_TYPES = new Set(['enum', 'multiselect', 'pipeline']);

/**
 * Normalizes a single option into a lens option shape.
 *
 * @param {object|string} option
 * @param {string} language
 * @returns {{ value: string, label: string }|null}
 */
const normalizeLensOption = (option, language) => {
    if (typeof option === 'string' || typeof option === 'number') {
        const value = String(option || '').trim();
        if (!value) return null;
        return {
            value,
            label: value,
        };
    }

    const value = String(option?.value || '').trim();
    if (!value) return null;

    const optionLabel = String(
        option?.translations?.[language]?.label
        || option?.translations?.en?.label
        || option?.label
        || value,
    ).trim() || value;

    return {
        value,
        label: optionLabel,
    };
};

/**
 * Builds a Tags lens field from currently loaded items.
 *
 * @param {Array} items
 * @returns {{ key: string, label: string, kind: string, options: Array<{ value: string, label: string }> }|null}
 */
export function buildTagsLensFieldFromItems(items) {
    const tagValues = new Set();

    (Array.isArray(items) ? items : []).forEach((item) => {
        const tags = Array.isArray(item?.tags) ? item.tags : [];
        tags.forEach((tag) => {
            const value = String(tag || '').trim();
            if (value) tagValues.add(value);
        });
    });

    if (!tagValues.size) return null;

    const options = [...tagValues]
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({
            value,
            label: value,
        }));

    return {
        key: 'tags',
        label: 'Tags',
        kind: 'tags',
        options,
    };
}

/**
 * Builds lens/facet field definitions for catch-all view toolbar filtering.
 *
 * @param {Array} resolvedProperties - Resolved schema fields for a node type.
 * @param {string} language - Active language key.
 * @param {object} [options]
 * @param {boolean} [options.includeMultiselect=false] - Include multiselect fields.
 * @param {Array} [options.items] - Current items used to derive Tags options.
 * @param {boolean} [options.includeTags=false] - Include Tags when items have tags.
 * @returns {Array<{ key: string, label: string, kind: string, options: Array<{ value: string, label: string }> }>}
 */
export default function buildCatchAllFacetFields(resolvedProperties, language, {
    includeMultiselect = false,
    items = null,
    includeTags = false,
} = {}) {
    const allowedTypes = includeMultiselect
        ? LENS_OPTION_FIELD_TYPES
        : new Set(['enum', 'pipeline']);

    const fields = (Array.isArray(resolvedProperties) ? resolvedProperties : [])
        .filter((field) => allowedTypes.has(String(field?.type || '').trim().toLowerCase()))
        .map((field) => {
            const key = String(field?.name || field?.key || '').trim();
            if (!key) return null;

            const kind = String(field?.type || '').trim().toLowerCase();
            const label = String(field?.label || '').trim() || key;
            const options = resolveEnumOptionsForField(field, language)
                .map((option) => normalizeLensOption(option, language))
                .filter(Boolean);

            if (!options.length) return null;

            return {
                key,
                label,
                kind,
                options,
            };
        })
        .filter(Boolean);

    if (includeTags) {
        const tagsField = buildTagsLensFieldFromItems(items);
        if (tagsField) fields.push(tagsField);
    }

    return fields;
}
